import { z } from "zod";

// Runtime configuration served at /config.json; one image serves every installation.
const RuntimeConfig = z.object({
  environment: z.enum(["development", "preview", "production"]),
  cosmosApiUrl: z.string(), // "" means same origin
  webApiUrl: z.string(),
  oidc: z
    .object({ authority: z.string(), clientId: z.string().min(1) })
    .transform((o) => (o.authority ? o : null)),
});
export type RuntimeConfig = z.infer<typeof RuntimeConfig>;

export class RuntimeConfigError extends Error {
  override name = "RuntimeConfigError";
}

/** Validates the raw configuration; an identity provider is required outside development. */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  const parsed = RuntimeConfig.safeParse(raw);
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ");
    throw new RuntimeConfigError(`invalid /config.json: ${issues}`);
  }
  const config = parsed.data;
  if (!config.oidc && config.environment !== "development") {
    throw new RuntimeConfigError("an identity provider is required outside development");
  }
  return config;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) throw new RuntimeConfigError(`/config.json answered ${response.status}`);
  const raw: unknown = await response.json().catch(() => {
    throw new RuntimeConfigError("/config.json is not JSON");
  });
  return parseRuntimeConfig(raw);
}
